/**
 * The art window the fingerprints were cut from, and the probe a client cuts it
 * with.
 *
 * ADR 0008 settled which rectangle the stored fingerprints describe: the
 * constant the pass-3 sweep fitted to them - 8% in from each side, 10.5% down,
 * 44% tall - and not the picture's own measured edges. The measurement places
 * the *picture* better; the constant is the better *cut*, because the printed
 * window barely moves between cards while Scryfall's crop has a shape of its
 * own, and a probe has to have that shape. The tool and the client therefore
 * have to cut the same rectangle, which is why it lives here rather than in
 * either of them.
 *
 * Two implementations of this exist on purpose and they must agree:
 *
 *  - `tools/catalog`'s `frameWindowHash` extracts the window with sharp, which
 *    is what every number in ADR 0006 and 0008 was measured through. Its
 *    real-image test compares this function against that one, card by card, so
 *    a divergence shows up as a failing assertion rather than as a scanner that
 *    quietly matches less often.
 *  - `artProbeHash` here is the same rectangle, the same 64x64 squash and the
 *    same fingerprint, in plain arithmetic: a browser has a canvas and no
 *    sharp, and a phone's device plugin will have neither.
 *
 * The probe is the only part of the cascade a *client* runs. Everything the
 * fingerprint is compared against - the index, the catalog, the fusion of the
 * two routes - happens where the data is.
 */
import { assertGreyImage, rectQuad, warpQuad } from './geometry.js';
import { PHASH_BITS, PHASH_SIZE, hashToHex, phashFromGrayLevels } from './phash.js';
/**
 * The window the fingerprints describe, in card fractions.
 *
 * `top` is where the picture starts and `height` how tall it is; the sides are
 * symmetric, so `inset` is both. These three numbers are the sweep's result and
 * a client must not invent its own: a probe cut anywhere else is compared
 * against fingerprints of this rectangle.
 */
export const INDEX_ART_WINDOW = { inset: 0.08, top: 0.105, height: 0.44 };
/**
 * How far a real capture's framing is expected to be off, in card fractions, and how
 * finely that range is searched.
 *
 * The fingerprint cliffs. Measured on a printed card's own image, sliding the window by
 * 1% (a third of a millimetre on a real card) takes it from 18 bits - a comfortable
 * match - to 40, which is past the 26-bit threshold and indistinguishable from a
 * different picture. And the constant window does not fit every printing: on
 * Exploding Barrel (tmc/39) the card's art runs 12.4%-58.2% where the window cuts
 * 10.5%-54.5%, so its fingerprint was 42 bits from its own entry in the index while the
 * same card's art crop was 2. And a capture's corners are good to a percent or two at
 * best: on 307 phone frames the client's detector lands a median 1.2% from the card and
 * up to 4% on the ones where the card fills the frame.
 *
 * So one cut is a gamble. A scan carries this grid of cuts instead - 5x5, +-4%, nominal
 * first - and the search takes each artwork's best framing. Measured: a capture whose
 * corners were 4% off went from 40 bits (no match) to 10 (matched), and one 7% off - the
 * card filling the frame, its edges clipped - from 40 to 22.
 */
export const PROBE_WINDOW_SHIFT = 0.02;
export const PROBE_WINDOW_STEPS = [-2, -1, 0, 1, 2];
/** The window as the rectangle the frame is measured in. */
export function indexWindowRect(window = INDEX_ART_WINDOW) {
    return {
        x0: window.inset,
        y0: window.top,
        x1: 1 - window.inset,
        y1: window.top + window.height,
    };
}
/**
 * The art window of a card-relative frame, at `size x size` grey levels.
 *
 * The rectangle is clamped to the frame before it is cut, exactly as the sharp
 * path clamps it with `extract`: a window that runs off the edge of a frame
 * would otherwise be resampled from nothing.
 */
export function artProbeLevels(frame, options = {}) {
    assertGreyImage(frame, 'the frame to probe');
    const size = options.size ?? PHASH_SIZE;
    if (size < 1)
        throw new Error(`a probe cannot be ${size} pixels wide`);
    const rect = options.rect ?? indexWindowRect();
    const { width, height } = frame;
    const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
    const left = clamp(Math.round(rect.x0 * width), 0, width - 1);
    const top = clamp(Math.round(rect.y0 * height), 0, height - 1);
    const right = clamp(Math.round(rect.x1 * width), left + 1, width);
    const bottom = clamp(Math.round(rect.y1 * height), top + 1, height);
    const art = warpQuad(frame, rectQuad({ x0: left, y0: top, x1: right, y1: bottom }), size, size);
    return art.levels;
}
/**
 * The grid of windows a scan probes, the nominal one first.
 *
 * Only `inset` and `top` move: a window that grew as it moved would sample a different
 * scale of the picture, and the index's fingerprints are of one scale. The height comes
 * along unchanged for the same reason.
 */
export function probeWindows(window = INDEX_ART_WINDOW) {
    const windows = [{ ...window }];
    for (const row of PROBE_WINDOW_STEPS) {
        for (const column of PROBE_WINDOW_STEPS) {
            if (row === 0 && column === 0)
                continue;
            windows.push({
                inset: window.inset + column * PROBE_WINDOW_SHIFT,
                top: window.top + row * PROBE_WINDOW_SHIFT,
                height: window.height,
            });
        }
    }
    return windows;
}
/** The fingerprint of a card frame's art window, as the index stores it. */
export function artProbeHash(frame, options = {}) {
    const size = options.size ?? PHASH_SIZE;
    const bits = options.bits ?? PHASH_BITS;
    return hashToHex(phashFromGrayLevels(artProbeLevels(frame, { ...options, size }), { size, bits }));
}
/**
 * The fingerprints of a frame's art window across the probe grid - what a scan sends.
 *
 * The first hash is the nominal window, so a caller that only wants one cut (`artPhash`)
 * is still reading the window the index was measured with.
 */
export function artProbeHashes(frame, options = {}) {
    return probeWindows().map((window) => artProbeHash(frame, { ...options, rect: indexWindowRect(window) }));
}
export function artProbe(frame, options = {}) {
    const rect = options.rect ?? indexWindowRect();
    return { frame, rect, hash: artProbeHash(frame, { ...options, rect }) };
}
//# sourceMappingURL=index-window.js.map